/**
 * CameraController.jsx - Simplified camera movement controller
 * Handles smooth camera animations to target positions and look-at points
 */

import React, { useState, useEffect, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { easeInOutCubic } from './CameraService.js'
import { ANIMATION_TIMINGS } from './CameraConfig.js'

// ============================================================================
// EVENT-DRIVEN CAMERA CONTROLLER
// ============================================================================

/**
 * CameraController - Listens for camera movement events and animates the camera
 * Dispatches 'scene-camera-move' custom events to trigger animations
 */
export function CameraController() {
  const [moveTarget, setMoveTarget] = useState(null)
  const [moveAnimate, setMoveAnimate] = useState(false)
  const [animationKey, setAnimationKey] = useState(0)

  useEffect(() => {
    function handleSceneCameraMove(event) {
      if (event.detail) {
        const {
          targetPosition,
          targetLookAt,
          duration = ANIMATION_TIMINGS.cameraMovement
        } = event.detail

        if (targetPosition && targetLookAt) {
          setMoveTarget({
            position: targetPosition,
            targetLookAt,
            duration
          })
          setMoveAnimate(true)
          setAnimationKey(prev => prev + 1) // Force remount for new animation
        }
      }
    }

    function handleCameraSnap(event) {
      if (event.detail) {
        const { targetPosition, targetLookAt } = event.detail
        // Stop current animation
        setMoveAnimate(false)
        // Then immediately start a new one with duration 1ms
        setMoveTarget({
          position: targetPosition,
          targetLookAt,
          duration: 1
        })
        setMoveAnimate(true)
        setAnimationKey(prev => prev + 1)
      }
    }

    window.addEventListener('scene-camera-move', handleSceneCameraMove)
    window.addEventListener('scene-camera-snap', handleCameraSnap)
    return () => {
      window.removeEventListener('scene-camera-move', handleSceneCameraMove)
      window.removeEventListener('scene-camera-snap', handleCameraSnap)
    }
  }, [])

  // Trigger animation when target is set
  return (
    <>
      {moveAnimate && moveTarget && (
        <CameraMovementController
          key={animationKey}
          targetPosition={moveTarget.position}
          targetLookAt={moveTarget.targetLookAt}
          animate={moveAnimate}
          duration={moveTarget.duration}
          onAnimationComplete={() => setMoveAnimate(false)}
        />
      )}
    </>
  )
}

// ============================================================================
// CAMERA MOVEMENT CONTROLLER
// ============================================================================

/**
 * CameraMovementController - Smooth animation of camera position and look-at target
 * @param {Object} props
 * @param {THREE.Camera} props.camera - The camera to animate (REQUIRED - must be passed, not from useThree)
 * @param {Object} props.controls - The controls object with target (REQUIRED - must be passed)
 * @param {THREE.Vector3} props.targetPosition - Target camera position
 * @param {THREE.Vector3} props.targetLookAt - Target look-at position
 * @param {boolean} props.animate - Whether to start animation
 * @param {number} props.duration - Animation duration in milliseconds
 * @param {Function} props.onAnimationComplete - Callback when animation finishes
 */
export function CameraMovementController({
  camera,
  controls,
  targetPosition,
  targetLookAt,
  animate = false,
  duration = ANIMATION_TIMINGS.cameraMovement,
  onAnimationComplete
}) {
  const [isAnimating, setIsAnimating] = useState(false)
  const [startPosition, setStartPosition] = useState(null)
  const [startTarget, setStartTarget] = useState(null)
  const animationStartTimeRef = useRef(null)

  // Initialize animation - ensure we're using cloned values to avoid reference issues
  useEffect(() => {
    if (animate && targetPosition && targetLookAt && camera && controls) {
      const clonedTargetPos = targetPosition.clone ? targetPosition.clone() : new THREE.Vector3().copy(targetPosition)
      const clonedTargetLookAt = targetLookAt.clone ? targetLookAt.clone() : new THREE.Vector3().copy(targetLookAt)
      
      setStartPosition(camera.position.clone())
      setStartTarget(controls.target ? controls.target.clone() : new THREE.Vector3(0, 0, 0))
      animationStartTimeRef.current = performance.now()
      setIsAnimating(true)

      // Disable controls during animation
      if (controls) {
        controls.enabled = false
      }
      
      // Store the cloned targets for animation
      if (!window.__cameraAnimTargets) {
        window.__cameraAnimTargets = {}
      }
      window.__cameraAnimTargets.targetPos = clonedTargetPos
      window.__cameraAnimTargets.targetLookAt = clonedTargetLookAt
    }
  }, [animate, targetPosition, targetLookAt, camera, controls])

  // Clean up animation
  useEffect(() => {
    if (!animate) {
      setIsAnimating(false)
      animationStartTimeRef.current = null
      setStartPosition(null)
      setStartTarget(null)

      // Re-enable controls
      if (controls) {
        controls.enabled = true
      }
    }
  }, [animate, controls])

  useFrame(() => {
    if (!isAnimating || !animationStartTimeRef.current || !startPosition || !startTarget || !camera) return

    const elapsed = performance.now() - animationStartTimeRef.current
    const progress = Math.min(elapsed / duration, 1)

    // Apply easing
    const easedProgress = easeInOutCubic(progress)

    // Get the target values (use window cache or props directly)
    const finalTargetPos = window.__cameraAnimTargets?.targetPos || targetPosition
    const finalTargetLookAt = window.__cameraAnimTargets?.targetLookAt || targetLookAt

    // If animation was force-cleared (orientation change), snap to target immediately
    const forcedCompletion = window.__forceAnimationComplete === true
    const effectiveProgress = forcedCompletion ? 1 : easedProgress

    // Interpolate camera position
    camera.position.lerpVectors(startPosition, finalTargetPos, effectiveProgress)

    // Interpolate look-at target
    if (controls && controls.target && startTarget && finalTargetLookAt) {
      controls.target.lerpVectors(startTarget, finalTargetLookAt, effectiveProgress)
      // DON'T call controls.update() during animation - it resets camera position!
      // controls.update() is called by OrbitControls on mouse movement, we don't need it here
    }

    // Animation complete
    if (progress >= 1 || forcedCompletion) {
      // Set final position
      camera.position.copy(finalTargetPos)
      
      if (controls && controls.target && finalTargetLookAt) {
        // Only update the target, don't call setLookAt which recalculates position
        controls.target.copy(finalTargetLookAt)
        controls.enabled = true
      }

      // Clear the forced completion flag
      window.__forceAnimationComplete = false

      setIsAnimating(false)
      animationStartTimeRef.current = null
      setStartPosition(null)
      setStartTarget(null)

      if (onAnimationComplete) {
        onAnimationComplete()
      }
    }
  })

  return null
}
